import React, { useEffect, useState } from 'react';
import {
    CANADIAN_PROVINCE_OPTIONS,
    isCanadianProvince,
    normalizeCanadianPostalCode,
    validateCanadianPostalCode,
} from '../../../../helpers/canadianLocation';
import { Feedback, useSectionFeedback } from './sectionFeedback';

const VISIBILITY_OPTIONS = [
    ['none', "Don't Show Any - Enable Distance Search Only"],
    ['country', 'Show my Country Only'],
    ['state', 'Show my State and Country'],
    ['county', 'Show my County, State and Country'],
    ['city', 'Show my City, State and Country'],
    ['address', 'Show my Full Address'],
];

const COUNTRY_OPTIONS = [
    ['US', 'United States'],
    ['CA', 'Canada'],
    ['GB', 'United Kingdom'],
    ['AU', 'Australia'],
    ['NZ', 'New Zealand'],
    ['IE', 'Ireland'],
];

const SUPPORTED_COUNTRY_CODES = new Set(COUNTRY_OPTIONS.map(([countryCode]) => countryCode));

const COUNTRY_DEFAULT_TIMEZONE = {
    US: 'America/New_York',
    CA: 'America/Toronto',
    GB: 'Europe/London',
    AU: 'Australia/Sydney',
    NZ: 'Pacific/Auckland',
    IE: 'Europe/Dublin',
};

const TIMEZONE_OPTIONS = [
    ['America/New_York', 'Eastern Time'],
    ['America/Chicago', 'Central Time'],
    ['America/Denver', 'Mountain Time'],
    ['America/Los_Angeles', 'Pacific Time'],
    ['America/Anchorage', 'Alaska Time'],
    ['Pacific/Honolulu', 'Hawaii Time'],
    ['America/Toronto', 'Toronto'],
    ['America/Vancouver', 'Vancouver'],
    ['America/Edmonton', 'Edmonton'],
    ['America/Winnipeg', 'Winnipeg'],
    ['America/Halifax', 'Halifax'],
    ['America/St_Johns', 'Newfoundland'],
    ['Europe/London', 'London'],
    ['Europe/Dublin', 'Dublin'],
    ['Australia/Sydney', 'Sydney'],
    ['Australia/Melbourne', 'Melbourne'],
    ['Australia/Brisbane', 'Brisbane'],
    ['Australia/Adelaide', 'Adelaide'],
    ['Australia/Darwin', 'Darwin'],
    ['Australia/Perth', 'Perth'],
    ['Pacific/Auckland', 'Auckland'],
    ['Pacific/Chatham', 'Chatham Islands'],
];

function normalizeCountryCode(value) {
    if (!value) return 'US';
    const normalized = String(value).trim().toUpperCase();
    if (normalized === 'UK') return 'GB';
    if (normalized === 'UNITED STATES') return 'US';
    return SUPPORTED_COUNTRY_CODES.has(normalized) ? normalized : 'US';
}

export default function LocationSection({ data, saving, onSave, actions }) {
    const location = data?.location || data?.basics?.location || {};
    const initialCountryCode = normalizeCountryCode(data?.countryCode || location.country);
    const initialProvince = initialCountryCode === 'CA' && isCanadianProvince(location.state)
        ? String(location.state).toUpperCase()
        : '';
    const [zipcode, setZipcode] = useState(location.zipcode || '');
    const [province, setProvince] = useState(initialProvince);
    const [locationVisibility, setLocationVisibility] = useState(location.visibility || 'none');
    const [address1, setAddress1] = useState(location.address1 || '');
    const [address2, setAddress2] = useState(location.address2 || '');
    const [countryCode, setCountryCode] = useState(initialCountryCode);
    const [timezone, setTimezone] = useState(data?.timezone || COUNTRY_DEFAULT_TIMEZONE[initialCountryCode]);
    const { feedback, showFeedback } = useSectionFeedback();

    useEffect(() => {
        const nextCountryCode = normalizeCountryCode(data?.countryCode || location.country);
        setZipcode(location.zipcode || '');
        setProvince(nextCountryCode === 'CA' && isCanadianProvince(location.state)
            ? String(location.state).toUpperCase()
            : '');
        setLocationVisibility(location.visibility || 'none');
        setAddress1(location.address1 || '');
        setAddress2(location.address2 || '');
        setCountryCode(nextCountryCode);
        setTimezone(data?.timezone || COUNTRY_DEFAULT_TIMEZONE[nextCountryCode]);
    }, [data?.countryCode, data?.timezone, location.country, location.zipcode, location.state,
        location.visibility, location.address1, location.address2]);

    function handleCountryChange(event) {
        const nextCountryCode = event.target.value;
        const currentCountryDefaultTimezone = COUNTRY_DEFAULT_TIMEZONE[countryCode];

        setCountryCode(nextCountryCode);
        if (nextCountryCode !== countryCode) {
            setZipcode('');
            setProvince('');
        }

        if (!timezone || timezone === currentCountryDefaultTimezone) {
            setTimezone(COUNTRY_DEFAULT_TIMEZONE[nextCountryCode]);
        }
    }

    function handleSave() {
        let normalizedZipcode = zipcode;

        if (countryCode === 'CA') {
            if (!province) {
                showFeedback('error', 'Select a province or territory.');
                return;
            }

            if (zipcode.trim()) {
                if (!validateCanadianPostalCode(zipcode)) {
                    showFeedback(
                        'error',
                        'Enter a valid Canadian postal code in A1A 1A1 format.'
                    );
                    return;
                }

                if (!validateCanadianPostalCode(zipcode, province)) {
                    showFeedback('error', 'Postal code does not match the selected province.');
                    return;
                }

                normalizedZipcode = normalizeCanadianPostalCode(zipcode);
                setZipcode(normalizedZipcode);
            }
        }

        onSave(async () => {
            const result = await actions.saveLocation({
                zipcode: normalizedZipcode,
                province: countryCode === 'CA' ? province : null,
                locationVisibility,
                address1,
                address2,
                countryCode,
                timezone,
            });
            showFeedback(result.success ? 'success' : 'error', result.success ? 'Location settings saved.' : result.error);
            return result;
        });
    }

    function handleRemove() {
        onSave(async () => {
            const result = await actions.removeLocation();
            showFeedback(result.success ? 'success' : 'error', result.success ? 'Location removed.' : result.error);
            return result;
        });
    }

    return (
        <div>
            <div className="sfm-section-header">
                <h2>Location</h2>
                <p>Make your store searchable by distance and choose what location details to display.</p>
            </div>
            <div className="group">
                <label htmlFor="sfm-country-code">Country</label>
                <select
                    id="sfm-country-code"
                    value={countryCode}
                    onChange={handleCountryChange}
                    aria-describedby="sfm-country-help"
                >
                    {COUNTRY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <p id="sfm-country-help" className="help">Sets the storefront market used for regional defaults.</p>
            </div>
            {countryCode === 'CA' ? (
                <div className="group">
                    <label htmlFor="sfm-province">Province or Territory</label>
                    <select
                        id="sfm-province"
                        value={province}
                        onChange={event => setProvince(event.target.value)}
                        aria-describedby="sfm-province-help"
                        required
                    >
                        <option value="">Select a province or territory</option>
                        {CANADIAN_PROVINCE_OPTIONS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <p id="sfm-province-help" className="help">
                        Used for Canadian address display and tax settings.
                    </p>
                </div>
            ) : null}
            <div className="group">
                <label htmlFor="sfm-timezone">Timezone</label>
                <select
                    id="sfm-timezone"
                    value={timezone}
                    onChange={event => setTimezone(event.target.value)}
                    aria-describedby="sfm-timezone-help"
                >
                    {TIMEZONE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <p id="sfm-timezone-help" className="help">
                    Used for seller-facing dates and times, and as the default for new booking
                    calendars.
                </p>
            </div>
            <div className="group">
                <label htmlFor="sfm-zipcode">{countryCode === 'US' ? 'Zip Code' : 'Postal Code'}</label>
                <input
                    id="sfm-zipcode"
                    type="text"
                    inputMode={countryCode === 'US' || countryCode === 'AU' || countryCode === 'NZ' ? 'numeric' : 'text'}
                    value={zipcode}
                    onChange={event => setZipcode(event.target.value)}
                    onBlur={() => {
                        if (countryCode === 'CA' && zipcode.trim()) {
                            setZipcode(normalizeCanadianPostalCode(zipcode));
                        }
                    }}
                    placeholder={countryCode === 'US'
                        ? '90210'
                        : (countryCode === 'CA' ? 'A1A 1A1' : 'Postal code')}
                    pattern={countryCode === 'CA'
                        ? '[ABCEGHJ-NPRSTVXYabceghj-nprstvxy][0-9]'
                            + '[ABCEGHJ-NPRSTV-Zabceghj-nprstv-z] [0-9]'
                            + '[ABCEGHJ-NPRSTV-Zabceghj-nprstv-z][0-9]'
                        : undefined}
                    aria-describedby="sfm-zipcode-help"
                />
                <p id="sfm-zipcode-help" className="help">
                    {countryCode === 'CA'
                        ? 'Use the A1A 1A1 format. The postal code must match the '
                            + 'selected province.'
                        : 'Used to enable location-aware storefront settings where supported.'}
                </p>
            </div>
            <div className="group">
                <label htmlFor="sfm-location-visibility">What to Show</label>
                <select
                    id="sfm-location-visibility"
                    value={locationVisibility}
                    onChange={event => setLocationVisibility(event.target.value)}
                    aria-describedby="sfm-visibility-help"
                >
                    {VISIBILITY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <p id="sfm-visibility-help" className="help">Controls which location details are publicly visible on your storefront.</p>
            </div>
            {locationVisibility === 'address' ? (
                <>
                    <div className="group">
                        <label htmlFor="sfm-address1">Address Line 1</label>
                        <input
                            id="sfm-address1"
                            type="text"
                            value={address1}
                            onChange={event => setAddress1(event.target.value)}
                            placeholder="123 Main Street"
                        />
                    </div>
                    <div className="group">
                        <label htmlFor="sfm-address2">Address Line 2</label>
                        <input
                            id="sfm-address2"
                            type="text"
                            value={address2}
                            onChange={event => setAddress2(event.target.value)}
                            placeholder="Suite 100, Apt B (optional)"
                        />
                    </div>
                </>
            ) : null}
            {location?.zipcode ? (
                <div className="sfm-current-location">
                    <strong>Storefront Location</strong>
                    <p>
                        {location.address1 ? <><strong>Address</strong>: {location.address1}<br /></> : null}
                        {location.address2 ? <><strong>Address 2</strong>: {location.address2}<br /></> : null}
                        {location.zipcode ? <><strong>{countryCode === 'US' ? 'Zip Code' : 'Postal Code'}</strong>: {location.zipcode}<br /></> : null}
                        {location.city ? <><strong>City</strong>: {location.city}<br /></> : null}
                        {location.county ? <><strong>County</strong>: {location.county}<br /></> : null}
                        {location.state ? (
                            <>
                                <strong>
                                    {countryCode === 'CA' ? 'Province/Territory' : 'State/Region'}
                                </strong>: {location.state}<br />
                            </>
                        ) : null}
                        <strong>Country</strong>: {location.country}
                    </p>
                    <button type="button" className="button secondary small" onClick={handleRemove} disabled={saving}>
                        Remove location information
                    </button>
                </div>
            ) : null}
            <div className="sfm-save-row">
                <button type="button" className="button primary" onClick={handleSave} disabled={saving}>Save Location</button>
                <Feedback feedback={feedback} />
            </div>
        </div>
    );
}
